import type { BeatLineViteConfig } from './types';

type AnyRecord = Record<string, any>;

const VERSION = 'beatline-local-cache-v2.2-prewarm-appdata-fetch+beatline_push_smart_user_link_dedupe_v1';
const SUMMARY_KEY = 'beatline_c74_full_migration_summary';
const SONG_META_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const HEARD_GET_TTL_MS = 60 * 1000;
const PUSH_REGISTER_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const PREWARM_CHUNK_SIZE = 50;
const PREWARM_CHUNK_DELAY_MS = 120;

const activePrewarms = new Set<string>();
let appDataFetchInFlight: Promise<AnyRecord | null> | null = null;

function now(): number {
  return Date.now();
}

function storageAvailable(): boolean {
  try {
    return typeof window !== 'undefined' && !!window.localStorage;
  } catch (_) {
    return false;
  }
}

function safeGet(key: string): any | null {
  if (!storageAvailable()) return null;

  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
}

function safeSet(key: string, value: any): void {
  if (!storageAvailable()) return;

  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (_) {}
}

function safeRemove(key: string): void {
  if (!storageAvailable()) return;

  try {
    localStorage.removeItem(key);
  } catch (_) {}
}

function cleanPart(value: string): string {
  return String(value || '')
    .trim()
    .replace(/[^a-zA-Z0-9._:-]+/g, '_')
    .slice(0, 160);
}

function asArray(value: any): string[] {
  if (value === undefined || value === null || value === '') return [];

  const values = Array.isArray(value) ? value : [value];

  return values
    .map((item) => String(item || '').trim())
    .filter(Boolean);
}

function firstValue(payload: AnyRecord, keys: string[]): string {
  for (const key of keys) {
    const values = asArray(payload?.[key]);
    if (values.length) return values[0];
  }
  return '';
}

function modeFromPayload(payload: AnyRecord): string {
  const mode = firstValue(payload || {}, ['mode', 'playback_mode', 'source']).toLowerCase();
  return ['spotify', 'youtube', 'itunes', 'preview'].includes(mode) ? (mode === 'preview' ? 'itunes' : mode) : 'itunes';
}

function playlistIdsFromPayload(payload: AnyRecord): string[] {
  const ids = [
    ...asArray(payload?.playlist_ids),
    ...asArray(payload?.['playlist_ids[]']),
    ...asArray(payload?.playlist_id),
    ...asArray(payload?.playlistId)
  ];

  return Array.from(new Set(ids.map(String).filter(Boolean)));
}

function songIdsFromPayload(payload: AnyRecord): string[] {
  const ids = [
    ...asArray(payload?.ids),
    ...asArray(payload?.['ids[]']),
    ...asArray(payload?.id),
    ...asArray(payload?.song_id),
    ...asArray(payload?.songId),
    ...asArray(payload?.track_id),
    ...asArray(payload?.trackId)
  ];

  return Array.from(new Set(ids.map(String).filter(Boolean)));
}

function trackIdsFromPayload(payload: AnyRecord): string[] {
  const ids = [
    ...asArray(payload?.track_ids),
    ...asArray(payload?.['track_ids[]']),
    ...asArray(payload?.track_id),
    ...asArray(payload?.trackId),
    ...asArray(payload?.id)
  ];

  return Array.from(new Set(ids.map(String).filter(Boolean)));
}

function songMetaKey(mode: string, id: string): string {
  return `beatline_lc_song_meta_${cleanPart(mode)}_${cleanPart(id)}`;
}

function heardGetKey(payload: AnyRecord): string {
  const mode = modeFromPayload(payload);
  const playlistIds = playlistIdsFromPayload(payload).join('__') || 'none';
  return `beatline_lc_heard_get_${cleanPart(mode)}_${cleanPart(playlistIds)}`;
}

function pushTokenFromPayload(payload: AnyRecord): string {
  return firstValue(payload || {}, [
    'token',
    'push_token',
    'pushToken',
    'device_token',
    'deviceToken',
    'fcm_token',
    'fcmToken',
    'registration_token',
    'registrationToken'
  ]);
}

function extractSongs(response: AnyRecord | null): AnyRecord[] {
  const paths = [
    response?.data?.songs,
    response?.data?.data?.songs,
    response?.songs,
    response?.data?.results?.['song-meta']?.data?.songs,
    response?.results?.['song-meta']?.data?.songs
  ];

  for (const value of paths) {
    if (Array.isArray(value)) return value.filter((item) => item && typeof item === 'object');
  }

  return [];
}

function extractHeard(response: AnyRecord | null): string[] {
  const heard = new Set<string>();

  function addList(value: any): void {
    if (!Array.isArray(value)) return;

    value
      .map((item) => String(item || '').trim())
      .filter(Boolean)
      .forEach((id) => heard.add(id));
  }

  // Oude/platte varianten blijven ondersteund.
  addList(response?.data?.heard);
  addList(response?.data?.data?.heard);
  addList(response?.heard);
  addList(response?.data?.results?.['heard-get']?.data?.heard);
  addList(response?.results?.['heard-get']?.data?.heard);

  // Werkelijke heard-get structuur:
  // { data: { playlists: { PLAYLIST_ID: { heard: [...] } } } }
  const playlistContainers = [
    response?.data?.playlists,
    response?.data?.data?.playlists,
    response?.playlists,
    response?.data?.results?.['heard-get']?.data?.playlists,
    response?.results?.['heard-get']?.data?.playlists
  ];

  playlistContainers.forEach((playlists) => {
    if (!playlists || typeof playlists !== 'object') return;

    Object.values(playlists).forEach((playlist: any) => {
      addList(playlist?.heard);
    });
  });

  return Array.from(heard);
}

function songIdFromSong(song: AnyRecord, fallback = ''): string {
  return String(
    song?.id ||
    song?.id_itunes ||
    song?.id_spotify ||
    song?.track_id ||
    song?.trackId ||
    fallback ||
    ''
  ).trim();
}

function validSongEnvelope(value: any, ttlMs: number): boolean {
  if (!value || typeof value !== 'object') return false;
  if (!value.song || typeof value.song !== 'object') return false;
  if (!Number.isFinite(Number(value.ts))) return false;
  return now() - Number(value.ts) <= ttlMs;
}

function validResponseEnvelope(value: any, ttlMs: number): boolean {
  if (!value || typeof value !== 'object') return false;
  if (!value.response || typeof value.response !== 'object') return false;
  if (!Number.isFinite(Number(value.ts))) return false;
  return now() - Number(value.ts) <= ttlMs;
}

function appDataFromResponse(response: AnyRecord | null): AnyRecord | null {
  const candidates = [
    response?.data?.playlists ? response.data : null,
    response?.playlists ? response : null,
    response?.data?.data?.playlists ? response.data.data : null,
    response?.data?.results?.['app-data']?.data?.playlists ? response.data.results['app-data'].data : null,
    response?.results?.['app-data']?.data?.playlists ? response.results['app-data'].data : null
  ];

  return candidates.find((item) => item && typeof item === 'object' && item.playlists) || null;
}

function playlistSongIdsFromAppData(appData: AnyRecord | null, playlistId: string, mode: string): string[] {
  if (!appData || !appData.playlists || !playlistId) return [];

  const playlist = appData.playlists[playlistId];
  if (!playlist || typeof playlist !== 'object') return [];

  const key = mode === 'preview' ? 'itunes' : mode;
  return asArray(playlist[key]);
}

function cachedSongExists(mode: string, id: string): boolean {
  return validSongEnvelope(safeGet(songMetaKey(mode, id)), SONG_META_TTL_MS);
}

function restBaseUrl(config: BeatLineViteConfig): string {
  const anyConfig = config as any;
  const base = String(anyConfig.remoteSiteUrl || anyConfig.siteUrl || window.location.origin || '').replace(/\/+$/, '');
  return base || window.location.origin;
}

function isNativeRuntime(config: BeatLineViteConfig): boolean {
  try {
    const w = window as any;
    const host = window.location.hostname || '';
    const protocol = window.location.protocol || '';
    const params = new URLSearchParams(window.location.search || '');

    if (w.Capacitor || w.BeatLineNative) return true;
    if (protocol === 'capacitor:' || protocol === 'file:') return true;
    if ((host === 'localhost' || host === '127.0.0.1') && params.get('native_app') === '1') return true;

    return false;
  } catch (_) {
    return false;
  }
}

async function fetchAndStoreAppData(config: BeatLineViteConfig): Promise<AnyRecord | null> {
  const existing = safeGet('beatline_lc_app_data')?.data || null;
  if (existing && typeof existing === 'object' && existing.playlists) {
    return existing;
  }

  if (appDataFetchInFlight) {
    return appDataFetchInFlight;
  }

  appDataFetchInFlight = (async (): Promise<AnyRecord | null> => {
    const url = new URL(`${restBaseUrl(config)}/wp-json/beatline/v1/shadow/batch`);
    url.searchParams.set('include', 'app-data');

    if (isNativeRuntime(config)) {
      url.searchParams.set('native_app', '1');
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`app-data HTTP ${response.status}`);
    }

    const json = await response.json();
    storeAppDataFromResponse(json);

    return appDataFromResponse(json);
  })().finally(() => {
    appDataFetchInFlight = null;
  });

  return appDataFetchInFlight;
}
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function updateSummarySnapshot(): void {
  if (!storageAvailable()) return;

  try {
    const raw = localStorage.getItem(SUMMARY_KEY);
    if (!raw) return;

    const parsed = JSON.parse(raw);
    parsed.localCache = safeCacheDebugSnapshot();
    localStorage.setItem(SUMMARY_KEY, JSON.stringify(parsed));
  } catch (_) {}
}

function prewarmStatsKey(): string {
  return 'beatline_lc_prewarm_stats';
}

function readPrewarmStats(): AnyRecord {
  const stats = safeGet(prewarmStatsKey());
  return stats && typeof stats === 'object'
    ? stats
    : {
        version: VERSION,
        started: 0,
        completed: 0,
        skippedNoAppData: 0,
        skippedNoIds: 0,
        skippedAlreadyRunning: 0,
        chunks: 0,
        requested: 0,
        stored: 0,
        skippedHeard: 0,
        skippedCached: 0,
        lastMs: 0,
        lastStatus: ''
      };
}

function writePrewarmStats(next: AnyRecord): void {
  safeSet(prewarmStatsKey(), {
    ...next,
    version: VERSION,
    updatedAt: new Date().toISOString()
  });
  updateSummarySnapshot();
}

export function localCacheVersion(): string {
  return VERSION;
}

export function storeAppDataFromResponse(response: AnyRecord): void {
  const data = appDataFromResponse(response);
  if (!data) return;

  safeSet('beatline_lc_app_data', {
    ts: now(),
    data
  });
}

export function getCachedSongMetaResponse(payload: AnyRecord): { response: AnyRecord | null; missingIds: string[]; songs: AnyRecord[] } {
  const mode = modeFromPayload(payload || {});
  const ids = songIdsFromPayload(payload || {});

  if (!ids.length) {
    return {
      response: {
        success: true,
        data: {
          songs: [],
          legacy_equivalent: 'beatline_get_song_meta'
        }
      },
      missingIds: [],
      songs: []
    };
  }

  const songs: AnyRecord[] = [];
  const missingIds: string[] = [];

  ids.forEach((id) => {
    const envelope = safeGet(songMetaKey(mode, id));

    if (validSongEnvelope(envelope, SONG_META_TTL_MS)) {
      songs.push(envelope.song);
    } else {
      missingIds.push(id);
    }
  });

  if (missingIds.length) {
    return {
      response: null,
      missingIds,
      songs
    };
  }

  return {
    response: {
      success: true,
      data: {
        songs,
        legacy_equivalent: 'beatline_get_song_meta'
      }
    },
    missingIds: [],
    songs
  };
}

export function payloadForMissingSongMeta(payload: AnyRecord, missingIds: string[]): AnyRecord {
  const next: AnyRecord = { ...(payload || {}) };

  delete next.ids;
  delete next['ids[]'];
  delete next.id;
  delete next.song_id;
  delete next.songId;
  delete next.track_id;
  delete next.trackId;

  next['ids[]'] = missingIds;

  return next;
}

export function storeSongMetaFromResponse(payload: AnyRecord, response: AnyRecord): void {
  const mode = modeFromPayload(payload || {});
  const requestedIds = songIdsFromPayload(payload || {});
  const songs = extractSongs(response);

  songs.forEach((song, index) => {
    const id = songIdFromSong(song, requestedIds[index] || '');
    if (!id) return;

    safeSet(songMetaKey(mode, id), {
      ts: now(),
      song
    });
  });
}

export function mergeSongMetaResponse(payload: AnyRecord, serverResponse: AnyRecord): AnyRecord {
  storeSongMetaFromResponse(payload, serverResponse);

  const cached = getCachedSongMetaResponse(payload);

  if (cached.response) return cached.response;

  return serverResponse;
}

export function getCachedHeardGet(payload: AnyRecord): AnyRecord | null {
  const envelope = safeGet(heardGetKey(payload || {}));

  if (!validResponseEnvelope(envelope, HEARD_GET_TTL_MS)) {
    return null;
  }

  return envelope.response;
}

export function storeHeardGet(payload: AnyRecord, response: AnyRecord): void {
  if (!response || response.success !== true || !response.data || typeof response.data !== 'object') {
    return;
  }

  safeSet(heardGetKey(payload || {}), {
    ts: now(),
    response
  });
}

export function invalidateHeardGet(payload: AnyRecord): void {
  safeRemove(heardGetKey(payload || {}));

  const mode = modeFromPayload(payload || {});
  const playlistIds = playlistIdsFromPayload(payload || {});

  if (!storageAvailable()) return;

  try {
    Object.keys(localStorage).forEach((key) => {
      if (!key.startsWith('beatline_lc_heard_get_')) return;

      if (playlistIds.length) {
        const joined = playlistIds.join('__');
        if (key.indexOf(cleanPart(joined)) !== -1) {
          localStorage.removeItem(key);
        }
        return;
      }

      if (key.indexOf(`beatline_lc_heard_get_${cleanPart(mode)}_`) === 0) {
        localStorage.removeItem(key);
      }
    });
  } catch (_) {}
}

function pushRegisterLinkedUserInfo(response: AnyRecord | null): { linked: boolean; userId: number } {
  const candidates = [
    response?.data,
    response?.data?.data,
    response
  ];

  for (const item of candidates) {
    if (!item || typeof item !== 'object') continue;

    const userId = Number(
      item.user_id ||
      item.userId ||
      item.linked_user_id ||
      item.linkedUserId ||
      0
    );

    const linked =
      item.linked_user === true ||
      item.linked_user === 1 ||
      item.linked_user === '1' ||
      item.user_linked === true ||
      item.user_linked === 1 ||
      item.user_linked === '1' ||
      userId > 0;

    if (linked && userId > 0) {
      return { linked: true, userId };
    }
  }

  return { linked: false, userId: 0 };
}
export function getFreshPushRegisterResponse(payload: AnyRecord): AnyRecord | null {
  const token = pushTokenFromPayload(payload || {});
  if (!token) return null;

  const envelope = safeGet('beatline_lc_push_register');

  if (!validResponseEnvelope(envelope, PUSH_REGISTER_TTL_MS)) return null;
  if (String(envelope.token || '') !== token) return null;

  // Smart dedupe: alleen lokaal overslaan als de server eerder heeft bevestigd
  // dat deze token gekoppeld is aan een echte ingelogde user.
  if (envelope.smartDedupeVersion !== 'beatline_push_smart_user_link_dedupe_v1') return null;
  if (envelope.linkedUser !== true) return null;
  if (Number(envelope.userId || 0) <= 0) return null;

  return envelope.response;
}

export function storePushRegisterResponse(payload: AnyRecord, response: AnyRecord): void {
  const token = pushTokenFromPayload(payload || {});
  if (!token) return;
  if (!response || response.success !== true) return;

  const linked = pushRegisterLinkedUserInfo(response);

  safeSet('beatline_lc_push_register', {
    ts: now(),
    token,
    response,
    linkedUser: linked.linked,
    userId: linked.userId,
    smartDedupeVersion: 'beatline_push_smart_user_link_dedupe_v1'
  });
}

export function schedulePrewarmFromHeardGet(payload: AnyRecord, heardResponse: AnyRecord, config: BeatLineViteConfig): void {
  try {
    const appDataEnvelope = safeGet('beatline_lc_app_data');
    let appData = appDataEnvelope?.data || null;
    const playlistIds = playlistIdsFromPayload(payload || {});
    const mode = modeFromPayload(payload || {});
    const heard = new Set(extractHeard(heardResponse).map(String));

    if (!appData) {
      const stats = readPrewarmStats();
      stats.lastStatus = 'fetching-app-data';
      writePrewarmStats(stats);

      void fetchAndStoreAppData(config)
        .then((freshAppData: AnyRecord | null) => {
          if (freshAppData && typeof freshAppData === 'object' && freshAppData.playlists) {
            schedulePrewarmFromHeardGet(payload, heardResponse, config);
            return;
          }

          const failed = readPrewarmStats();
          failed.skippedNoAppData = Number(failed.skippedNoAppData || 0) + 1;
          failed.lastStatus = 'skipped-no-app-data';
          writePrewarmStats(failed);
        })
        .catch((e: unknown) => {
          const failed = readPrewarmStats();
          failed.skippedNoAppData = Number(failed.skippedNoAppData || 0) + 1;
          failed.lastStatus = e instanceof Error ? `app-data:${e.message}` : 'app-data-fetch-failed';
          writePrewarmStats(failed);
        });

      return;
    }

    playlistIds.forEach((playlistId) => {
      const prewarmKey = `${mode}__${playlistId}`;

      if (activePrewarms.has(prewarmKey)) {
        const stats = readPrewarmStats();
        stats.skippedAlreadyRunning = Number(stats.skippedAlreadyRunning || 0) + 1;
        stats.lastStatus = 'skipped-already-running';
        writePrewarmStats(stats);
        return;
      }

      const allIds = playlistSongIdsFromAppData(appData, playlistId, mode);

      if (!allIds.length) {
        const stats = readPrewarmStats();
        stats.skippedNoIds = Number(stats.skippedNoIds || 0) + 1;
        stats.lastStatus = 'skipped-no-ids';
        writePrewarmStats(stats);
        return;
      }

      const missing = allIds.filter((id) => !heard.has(String(id)) && !cachedSongExists(mode, id));
      const skippedHeard = allIds.filter((id) => heard.has(String(id))).length;
      const skippedCached = allIds.filter((id) => !heard.has(String(id)) && cachedSongExists(mode, id)).length;

      if (!missing.length) {
        const stats = readPrewarmStats();
        stats.started = Number(stats.started || 0) + 1;
        stats.completed = Number(stats.completed || 0) + 1;
        stats.skippedHeard = Number(stats.skippedHeard || 0) + skippedHeard;
        stats.skippedCached = Number(stats.skippedCached || 0) + skippedCached;
        stats.lastStatus = 'complete-no-missing';
        writePrewarmStats(stats);
        return;
      }

      activePrewarms.add(prewarmKey);
      void prewarmMissingSongMeta({
        playlistId,
        mode,
        missing,
        skippedHeard,
        skippedCached,
        config
      }).finally(() => {
        activePrewarms.delete(prewarmKey);
      });
    });
  } catch (_) {}
}

async function prewarmMissingSongMeta(args: {
  playlistId: string;
  mode: string;
  missing: string[];
  skippedHeard: number;
  skippedCached: number;
  config: BeatLineViteConfig;
}): Promise<void> {
  const startedAt = now();
  const stats = readPrewarmStats();

  stats.started = Number(stats.started || 0) + 1;
  stats.requested = Number(stats.requested || 0) + args.missing.length;
  stats.skippedHeard = Number(stats.skippedHeard || 0) + args.skippedHeard;
  stats.skippedCached = Number(stats.skippedCached || 0) + args.skippedCached;
  stats.lastStatus = 'running';
  writePrewarmStats(stats);

  let chunks = 0;
  let stored = 0;

  try {
    for (let i = 0; i < args.missing.length; i += PREWARM_CHUNK_SIZE) {
      const chunk = args.missing.slice(i, i + PREWARM_CHUNK_SIZE);
      chunks += 1;

      const url = new URL(`${restBaseUrl(args.config)}/wp-json/beatline/v1/shadow/batch`);
      url.searchParams.set('include', 'song-meta');
      url.searchParams.set('playlist_id', args.playlistId);
      url.searchParams.set('mode', args.mode);

      if (isNativeRuntime(args.config)) {
        url.searchParams.set('native_app', '1');
      }

      chunk.forEach((id) => url.searchParams.append('ids[]', id));

      const response = await fetch(url.toString(), {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`prewarm HTTP ${response.status}`);
      }

      const json = await response.json();
      const payload = {
        playlist_id: args.playlistId,
        mode: args.mode,
        'ids[]': chunk
      };

      storeSongMetaFromResponse(payload, json);
      stored += extractSongs(json).length;

      const nextStats = readPrewarmStats();
      nextStats.chunks = Number(nextStats.chunks || 0) + 1;
      nextStats.stored = Number(nextStats.stored || 0) + extractSongs(json).length;
      nextStats.lastMs = now() - startedAt;
      nextStats.lastStatus = 'running';
      writePrewarmStats(nextStats);

      if (i + PREWARM_CHUNK_SIZE < args.missing.length) {
        await sleep(PREWARM_CHUNK_DELAY_MS);
      }
    }

    const done = readPrewarmStats();
    done.completed = Number(done.completed || 0) + 1;
    done.lastMs = now() - startedAt;
    done.lastStatus = `completed:${stored}`;
    writePrewarmStats(done);
  } catch (e) {
    const failed = readPrewarmStats();
    failed.lastMs = now() - startedAt;
    failed.lastStatus = e instanceof Error ? e.message : 'prewarm-failed';
    writePrewarmStats(failed);
  }
}

export function safeCacheDebugSnapshot(): AnyRecord {
  if (!storageAvailable()) {
    return {
      version: VERSION,
      available: false
    };
  }

  let songMetaCount = 0;
  let heardGetCount = 0;
  let hasPushRegister = false;

  try {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('beatline_lc_song_meta_')) songMetaCount += 1;
      if (key.startsWith('beatline_lc_heard_get_')) heardGetCount += 1;
      if (key === 'beatline_lc_push_register') hasPushRegister = true;
    });
  } catch (_) {}

  return {
    version: VERSION,
    available: true,
    songMetaCount,
    heardGetCount,
    hasPushRegister,
    prewarm: readPrewarmStats()
  };
}

export function heardCommitTrackIds(payload: AnyRecord): string[] {
  return trackIdsFromPayload(payload || {});
}